/**
 * Copia de seguridad manual (semanal) desde el móvil.
 *
 * La copia es el mismo .zip de «Exportar» (db.sqlite + metadata.json), que se
 * restaura con Importar archivo → «Archivo de Presupuesto (.zip)». En Chrome
 * Android se abre la hoja de compartir del sistema con el archivo para que
 * Vadym elija «Guardar en Drive»; si no se puede compartir, se descarga.
 *
 * Aquí solo hay lógica pura (testeable): nombre del archivo, decisión
 * compartir/descargar y cuándo toca recordar la copia. Las fechas son días
 * locales `AAAA-MM-DD` (preferencia sincronizada `copia-ultima-fecha`).
 */

/** Cada cuántos días se recuerda hacer la copia. */
export const DIAS_ENTRE_COPIAS = 7;

/** Día local `AAAA-MM-DD`. */
export function diaLocal(fecha: Date): string {
  const a = fecha.getFullYear();
  const m = String(fecha.getMonth() + 1).padStart(2, '0');
  const d = String(fecha.getDate()).padStart(2, '0');
  return `${a}-${m}-${d}`;
}

/** `rumbo-copia-AAAA-MM-DD.zip` */
export function nombreArchivoCopia(hoy: Date): string {
  return `rumbo-copia-${diaLocal(hoy)}.zip`;
}

const RE_DIA = /^(\d{4})-(\d{2})-(\d{2})$/;

function aUtc(dia: string): number | null {
  const m = RE_DIA.exec(dia);
  if (!m) {
    return null;
  }
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * Días enteros entre `desde` y `hoy` (ambos `AAAA-MM-DD`). `null` si `desde`
 * no es una fecha válida (o no hay). Nunca negativo.
 */
export function diasDesde(
  desde: string | null | undefined,
  hoy: string,
): number | null {
  if (!desde) {
    return null;
  }
  const a = aUtc(desde);
  const b = aUtc(hoy);
  if (a === null || b === null) {
    return null;
  }
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/**
 * Estado del contador de Inicio:
 * - `reciente`: 0-4 días (verde);
 * - `pronto`: 5-6 días, se acerca el día (ámbar);
 * - `toca`: 7 días o más (aviso);
 * - `nunca`: no hay ninguna copia (aviso).
 */
export type NivelCopia = 'reciente' | 'pronto' | 'toca' | 'nunca';

/** A partir de cuántos días el contador pasa a ámbar. */
export const DIAS_PRONTO = 5;

export type EstadoContadorCopia = {
  /** Días desde la última copia (`null` = nunca). */
  dias: number | null;
  nivel: NivelCopia;
  /** 0..1: cuánto se ha llenado el anillo hacia los 7 días. */
  fraccion: number;
  /** Día en que toca la siguiente (`AAAA-MM-DD`), última + 7. */
  siguiente: string | null;
  /** Días que faltan para la siguiente (0 = hoy; negativo = ya pasó). */
  faltan: number | null;
};

/** `dia` + `n` días (`AAAA-MM-DD`). */
export function sumarDias(dia: string, n: number): string | null {
  const t = aUtc(dia);
  if (t === null) {
    return null;
  }
  const f = new Date(t + n * 86_400_000);
  const a = f.getUTCFullYear();
  const m = String(f.getUTCMonth() + 1).padStart(2, '0');
  const d = String(f.getUTCDate()).padStart(2, '0');
  return `${a}-${m}-${d}`;
}

export function estadoContadorCopia(
  ultima: string | null | undefined,
  hoy: string,
): EstadoContadorCopia {
  const dias = diasDesde(ultima, hoy);
  if (dias === null || !ultima) {
    return {
      dias: null,
      nivel: 'nunca',
      fraccion: 1,
      siguiente: null,
      faltan: null,
    };
  }
  const nivel: NivelCopia =
    dias >= DIAS_ENTRE_COPIAS
      ? 'toca'
      : dias >= DIAS_PRONTO
        ? 'pronto'
        : 'reciente';
  return {
    dias,
    nivel,
    fraccion: Math.min(1, dias / DIAS_ENTRE_COPIAS),
    siguiente: sumarDias(ultima, DIAS_ENTRE_COPIAS),
    faltan: DIAS_ENTRE_COPIAS - dias,
  };
}

/** Día `AAAA-MM-DD` como `Date` local (para el nombre del día de la semana). */
export function fechaLocal(dia: string): Date | null {
  const m = RE_DIA.exec(dia);
  if (!m) {
    return null;
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Lo mínimo de `navigator` que hace falta (para poder probarlo). */
export type NavegadorCompartir = {
  canShare?: (datos: { files: File[] }) => boolean;
  share?: (datos: { files: File[]; title?: string }) => Promise<void>;
};

/** ¿Se puede abrir la hoja de compartir del sistema con este archivo? */
export function puedeCompartirArchivo(
  nav: NavegadorCompartir | undefined,
  archivo: File,
): boolean {
  try {
    return (
      typeof nav?.share === 'function' &&
      nav.canShare?.({ files: [archivo] }) === true
    );
  } catch {
    return false;
  }
}

/**
 * - `compartida`: la hoja de compartir terminó sin error (cuenta como hecha).
 * - `descargada`: no se podía compartir y se ha descargado (cuenta como hecha).
 * - `cancelada`: cerró la hoja de compartir sin elegir nada (AbortError).
 * - `fallo-compartir`: la hoja no se pudo abrir (p. ej. se tardó demasiado en
 *   preparar el archivo); se ofrece «Descargar» o volver a intentarlo.
 */
export type ResultadoGuardado =
  | 'compartida'
  | 'descargada'
  | 'cancelada'
  | 'fallo-compartir';

export async function guardarCopia({
  archivo,
  titulo,
  nav,
  descargar,
}: {
  archivo: File;
  titulo: string;
  nav: NavegadorCompartir | undefined;
  descargar: () => Promise<void> | void;
}): Promise<ResultadoGuardado> {
  if (puedeCompartirArchivo(nav, archivo) && nav?.share) {
    try {
      await nav.share({ files: [archivo], title: titulo });
      return 'compartida';
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        return 'cancelada';
      }
      return 'fallo-compartir';
    }
  }
  await descargar();
  return 'descargada';
}

/** ¿Este resultado cuenta como copia hecha (se apunta la fecha)? */
export function cuentaComoHecha(r: ResultadoGuardado): boolean {
  return r === 'compartida' || r === 'descargada';
}
